import { createContext, useContext, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@/lib/tableColumns";
import type { ColumnFilterDef } from "@/lib/columnFilters";
import { ColumnsMenu } from "./ColumnsMenu";
import { ActiveFilters, ColumnFilterButton, useLocalListFilters, useUrlListFilters, type ListFilters } from "./ColumnFilter";
import { useColumnLayout, type ColumnLayoutOptions } from "./useColumnLayout";
import { useSessionState } from "@/lib/sessionState";

// A list whose columns people arrange themselves and sort by clicking a header
// (Camiel, 2026-09-17: first Taken, then "relaties kunnen sorteren, idem bij
// locaties en kijk overal", with "kolominstellingen beheren, dus verplaatsen, aan
// en uit zetten"). The layout rules are lib/tableColumns.ts and its wiring
// useColumnLayout.ts; this is the header, the table around it and the sort state
// in the address bar, shared by the lists so they behave alike.

export interface ListColumn extends ColumnDef {
  labelKey: string;
  /** The key the list's API sorts on; a column without one does not sort. */
  sortKey?: string;
  /** What the tooltip says it sorts on, when that is not the header itself. */
  sortLabelKey?: string;
  /** Newest or largest first on the first click, as dates and counts read best. */
  descFirst?: boolean;
  /** Numbers line up on the right. */
  align?: "right";
  /** What the column filters on (21-09-2026): the icon in its header. Sortable columns have one. */
  filter?: ColumnFilterDef;
  /** The key it filters on, filter.<key>, when that is not the sort key. */
  filterKey?: string;
}

/** The key a column filters on: its own, else what it sorts on, else its id. */
export function filterKeyOf(column: ListColumn): string {
  return column.filterKey ?? column.sortKey ?? column.id;
}

/** Per filter key, the kind of filter — for a list that filters its own rows (lib/columnFilters.ts filterRows). */
export function filterKinds(columns: readonly ListColumn[]): Record<string, ColumnFilterDef["kind"]> {
  return Object.fromEntries(columns.filter((column) => column.filter).map((column) => [filterKeyOf(column), column.filter!.kind]));
}

export type ColumnArrangement<T extends ColumnDef> = ReturnType<typeof useColumnLayout<T>>;

export type SortOrder = "asc" | "desc";

export interface ListSort {
  sort: string;
  order: SortOrder;
  toggle: (column: ListColumn) => void;
}

/**
 * The sort in the address bar, so a back link or a shared link keeps it. The
 * list's own default leaves no trace there.
 */
export function useListSort(
  search: { sort?: string; order?: string },
  defaults: { sort: string; order: SortOrder },
  updateParams: (updates: Record<string, string>) => void,
): ListSort {
  const sort = search.sort ?? defaults.sort;
  const order: SortOrder = search.order === "asc" || search.order === "desc" ? search.order : search.sort ? "asc" : defaults.order;
  return {
    sort,
    order,
    toggle: (column) => {
      if (!column.sortKey) return;
      const next: SortOrder = sort === column.sortKey ? (order === "asc" ? "desc" : "asc") : column.descFirst ? "desc" : "asc";
      const isDefault = column.sortKey === defaults.sort && next === defaults.order;
      updateParams({ sort: isDefault ? "" : column.sortKey, order: isDefault ? "" : next, cursor: "" });
    },
  };
}

/** The column layout of a list, its sort and its column filters, together. */
export function useListTable<T extends ListColumn>(
  table: string,
  defs: readonly T[],
  userId: string,
  search: { sort?: string; order?: string },
  defaults: { sort: string; order: SortOrder },
  updateParams: (updates: Record<string, string>) => void,
  options: ColumnLayoutOptions = {},
) {
  const columns = useColumnLayout(table, defs, userId, options);
  const sort = useListSort(search, defaults, updateParams);
  const filters = useUrlListFilters(search, updateParams);
  return { columns, sort, filters };
}

const ListMemoryScope = createContext<string | null>(null);

/**
 * The lists loaded whole under it keep their sort and filters in this tab while
 * the screen is gone, under `scope` and their table (lib/sessionState): a step
 * of Plannen comes back as it was left.
 */
export function ListMemory({ scope, children }: { scope: string; children: ReactNode }) {
  return <ListMemoryScope.Provider value={scope}>{children}</ListMemoryScope.Provider>;
}

/**
 * The same for a list that is loaded whole and sorted in the browser
 * (lib/sortRows.ts): the sort lives in the screen, not in the address bar,
 * and under a ListMemory also while the screen is gone.
 */
export function useLocalListTable<T extends ListColumn>(
  table: string,
  defs: readonly T[],
  userId: string,
  defaults: { sort: string; order: SortOrder },
  options: ColumnLayoutOptions = {},
) {
  const scope = useContext(ListMemoryScope);
  const memory = scope && `${scope}:${table}`;
  const columns = useColumnLayout(table, defs, userId, options);
  const filters = useLocalListFilters(memory && `${memory}:filters`);
  const [state, setState] = useSessionState<{ sort: string; order: SortOrder }>(memory && `${memory}:sort`, defaults);
  const sort: ListSort = {
    ...state,
    toggle: (column) => {
      if (!column.sortKey) return;
      const key = column.sortKey;
      setState((current) => ({
        sort: key,
        order: current.sort === key ? (current.order === "asc" ? "desc" : "asc") : column.descFirst ? "desc" : "asc",
      }));
    },
  };
  return { columns, sort, filters };
}

/**
 * How many cells a full-width row spans: the columns shown, the filler, and
 * `extra` for a fixed column of the list's own, such as its actions.
 */
export function columnSpan<T extends ColumnDef>(columns: ColumnArrangement<T>, extra = 0): number {
  return columns.visible.length + (columns.fixed ? 1 : 0) + extra;
}

/** The empty cell at the end of a row once widths are fixed. */
export function FillerCell<T extends ColumnDef>({ columns }: { columns: ColumnArrangement<T> }) {
  return columns.fixed ? <td className="wl-table__filler" aria-hidden="true" /> : null;
}

/** "Kolommen" for the list's toolbar. */
export function ListColumnsMenu<T extends ListColumn>({ columns }: { columns: ColumnArrangement<T> }) {
  const { t } = useTranslation();
  return (
    <ColumnsMenu
      items={columns.ordered.map((column) => ({
        id: column.id,
        label: t(column.labelKey as never),
        shown: column.locked === true || !columns.layout.hidden.includes(column.id),
        locked: column.locked,
      }))}
      onToggle={columns.toggle}
      onMove={columns.move}
      onMoveBy={columns.moveBy}
      onReset={columns.reset}
      canReset={!columns.isDefault}
    />
  );
}

/**
 * A header cell: dragged by its label to another place, sized by its right edge,
 * sorted by a click when the list can sort on it, and filtered from its icon.
 */
export function ColumnHeader<T extends ListColumn>({
  column,
  columns,
  sort,
  filters,
}: {
  column: T;
  columns: ColumnArrangement<T>;
  sort: ListSort;
  filters?: ListFilters;
}) {
  const { t } = useTranslation();
  const label = t(column.labelKey as never);
  const sortable = column.sortKey !== undefined;
  const isActive = sortable && sort.sort === column.sortKey;
  const alignment = column.align === "right" ? { justifyContent: "flex-end" } : undefined;
  const filterable = filters !== undefined && column.filter !== undefined;
  return (
    <th
      data-column={column.id}
      className={`is-sortable${filterable ? " is-filterable" : ""}${filterable && filters!.values[filterKeyOf(column)] ? " is-filtered" : ""} ${columns.headerClassName(column.id)}`}
      aria-sort={sortable ? (isActive ? (sort.order === "asc" ? "ascending" : "descending") : "none") : undefined}
      {...columns.dropTargetProps(column.id)}
    >
      {sortable ? (
        <button
          type="button"
          className={`wl-th-sort${isActive ? " is-active" : ""}`}
          style={alignment}
          onClick={() => sort.toggle(column)}
          title={t("columns.sortBy", { column: column.sortLabelKey ? t(column.sortLabelKey as never) : label })}
          {...columns.dragSourceProps(column.id)}
        >
          <span className="wl-th-sort__label">{label}</span>
          <span className={`wl-th-sort__arr${isActive ? "" : " wl-th-sort__arr--muted"}`} aria-hidden="true">
            {isActive ? (sort.order === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </button>
      ) : (
        <span
          className="wl-th-sort wl-th-sort--static"
          style={alignment}
          title={t("columns.dragHint")}
          {...columns.dragSourceProps(column.id)}
        >
          <span className="wl-th-sort__label">{label}</span>
        </span>
      )}
      {filterable && <ColumnFilterButton columnKey={filterKeyOf(column)} label={label} def={column.filter!} filters={filters!} />}
      <span
        {...columns.resizeHandleProps(column.id)}
        role="separator"
        aria-orientation="vertical"
        aria-label={t("columns.resize", { column: label })}
        title={t("columns.resizeHint")}
      />
    </th>
  );
}

/**
 * The table of a list with arranged columns: its widths once someone sized a
 * column, the header row, and the body the list draws itself — each row with a
 * cell per shown column, then the `trailing` cell when there is one, and a
 * FillerCell at the end.
 *
 * `trailing` is a column the list keeps to itself after the arranged ones, such
 * as the buttons of a row: never moved, hidden or sized, and named only for
 * screen readers. `leading` is its like before them: the checkbox of a list
 * whose rows can be picked (.wl-table__check), with what its header holds.
 */
export function ListTable<T extends ListColumn>({
  columns,
  sort,
  filters,
  filterDefs,
  trailing,
  leading,
  children,
}: {
  columns: ColumnArrangement<T>;
  sort: ListSort;
  /** The column filters; without them the headers have no filter icon. */
  filters?: ListFilters;
  /**
   * A filter whose choices come from data — the roles there are — by column id, in place of
   * the column's own. The column definitions stay the stable array useColumnLayout wants.
   */
  filterDefs?: Readonly<Record<string, ColumnFilterDef>>;
  trailing?: { label: string; width: number };
  leading?: { header: ReactNode; width: number };
  children: ReactNode;
}) {
  const minWidth = columns.tableWidth + (trailing?.width ?? 0) + (leading?.width ?? 0);
  const withFilter = (column: T): T => (filterDefs?.[column.id] ? { ...column, filter: filterDefs[column.id] } : column);
  return (
    <>
      {filters && <ActiveFilters columns={columns.ordered.map(withFilter)} keyOf={filterKeyOf} filters={filters} />}
      {/* It scrolls itself, down under a header row that stays and sideways with its scrollbar
          in view; on a list screen it fills the window (.wl-page--list, 22-09-2026). */}
      <div className="wl-table-wrap">
        <table
          className={`wl-table${columns.fixed ? " wl-table--fixed" : ""}${columns.resizing ? " is-resizing" : ""}`}
          // Fixed widths: at least the sum of the columns, so a table made wider
          // than the screen scrolls instead of squeezing them back.
          style={columns.fixed ? { minWidth: `${minWidth}px` } : undefined}
        >
          {columns.fixed && (
            <colgroup>
              {leading && <col style={{ width: `${leading.width}px` }} />}
              {columns.visible.map((column) => (
                <col key={column.id} style={{ width: `${columns.widthOf(column)}px` }} />
              ))}
              {trailing && <col style={{ width: `${trailing.width}px` }} />}
              <col />
            </colgroup>
          )}
          <thead>
            <tr>
              {leading && <th className="wl-table__check">{leading.header}</th>}
              {columns.visible.map((column) => (
                <ColumnHeader key={column.id} column={withFilter(column)} columns={columns} sort={sort} filters={filters} />
              ))}
              {trailing && <th aria-label={trailing.label} />}
              {columns.fixed && <th className="wl-table__filler" aria-hidden="true" />}
            </tr>
          </thead>
          {children}
        </table>
      </div>
    </>
  );
}
